import React, { useState, useEffect } from 'react';
import { Trophy, Medal, Star, ArrowUp, ArrowDown, RefreshCw, Filter } from 'lucide-react';
import { gameAPI } from '../services/api';

const LeaderboardTab = ({ selectedTab }) => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hotelType, setHotelType] = useState('all');
  const [sortBy, setSortBy] = useState('score');
  const [sortOrder, setSortOrder] = useState('desc');

  // Fetch leaderboard data
  const fetchLeaderboard = async () => {
    setLoading(true);
    setError(null);
    
    try {
      let response;
      
      if (hotelType === 'all') {
        response = await gameAPI.getLeaderboard();
      } else {
        response = await gameAPI.getLeaderboardByType(hotelType);
      }
      
      setLeaderboard(response.data.leaderboard || []);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      setError('Failed to load leaderboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Load leaderboard when tab is selected or filters change
  useEffect(() => {
    if (selectedTab === 'leaderboard') {
      fetchLeaderboard();
    }
  }, [selectedTab, hotelType]);

  // Handle sorting
  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle sort order
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, set default to descending
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  // Get sorted leaderboard data
  const getSortedLeaderboard = () => {
    return [...leaderboard].sort((a, b) => {
      let valueA = a[sortBy];
      let valueB = b[sortBy];
      
      // Handle special sort cases
      if (sortBy === 'hotelName' || sortBy === 'username' || sortBy === 'hotelType') {
        valueA = String(valueA).toLowerCase();
        valueB = String(valueB).toLowerCase();
      } else {
        valueA = Number(valueA);
        valueB = Number(valueB);
      }
      
      if (sortOrder === 'asc') {
        return valueA > valueB ? 1 : -1;
      } else {
        return valueA < valueB ? 1 : -1;
      }
    });
  };

  // Render sort arrow
  const renderSortArrow = (column) => {
    if (sortBy !== column) return null;
    
    return sortOrder === 'asc' ? 
      <ArrowUp className="w-3 h-3 inline ml-1" /> : 
      <ArrowDown className="w-3 h-3 inline ml-1" />;
  };

  // Skip rendering if not selected
  if (selectedTab !== 'leaderboard') return null;

  return (
    <div>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-4">
        <h2 className="text-xl font-bold flex items-center">
          <Trophy className="w-6 h-6 text-yellow-500 mr-2" />
          Hotel Hero Leaderboard
        </h2>
        
        <div className="flex flex-wrap gap-2">
          {/* Hotel type filter */}
          <div className="relative">
            <select
              value={hotelType}
              onChange={(e) => setHotelType(e.target.value)}
              className="bg-white border border-gray-300 rounded-md px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Hotel Types</option>
              <option value="standard">Standard</option>
              <option value="budget">Budget</option>
              <option value="luxury">Luxury</option>
              <option value="boutique">Boutique</option>
            </select>
            <Filter className="w-4 h-4 text-gray-500 absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" />
          </div>
          
          {/* Refresh button */}
          <button 
            onClick={fetchLeaderboard} 
            disabled={loading}
            className={`flex items-center bg-blue-600 text-white px-3 py-2 rounded-md text-sm transition-colors ${loading ? 'opacity-70' : 'hover:bg-blue-700'}`}
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>
      
      {/* Error message */}
      {error && (
        <div className="bg-red-100 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      {/* Leaderboard table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading && leaderboard.length === 0 ? (
          <div className="flex justify-center items-center p-12">
            <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
            <span className="ml-3 text-gray-600">Loading leaderboard...</span>
          </div>
        ) : leaderboard.length === 0 ? (
          <div className="text-center py-12 bg-blue-50">
            <Trophy className="w-16 h-16 text-yellow-400 mx-auto mb-4 opacity-50" />
            <h3 className="text-lg font-medium text-gray-600">No leaderboard data yet!</h3>
            <p className="text-gray-500 mt-2">Save your games to see them on the leaderboard.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rank
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('username')}
                  >
                    Player {renderSortArrow('username')}
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('hotelName')}
                  >
                    Hotel {renderSortArrow('hotelName')}
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('hotelType')}
                  >
                    Type {renderSortArrow('hotelType')}
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('day')}
                  >
                    Days {renderSortArrow('day')}
                  </th>
                  <th 
                    className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('money')}
                  >
                    Money {renderSortArrow('money')}
                  </th>
                  <th 
                    className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('reputation')}
                  >
                    Rating {renderSortArrow('reputation')}
                  </th>
                  <th 
                    className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('marketShare')}
                  >
                    Market {renderSortArrow('marketShare')}
                  </th>
                  <th 
                    className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('score')}
                  >
                    Score {renderSortArrow('score')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {getSortedLeaderboard().map((entry, index) => (
                  <tr 
                    key={entry.id} 
                    className={`${index < 3 ? 'bg-yellow-50' : index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition-colors`}
                  >
                    <td className="px-4 py-3 whitespace-nowrap font-medium">
                      {index === 0 ? (
                        <div className="flex items-center">
                          <Medal className="w-5 h-5 text-yellow-500 fill-yellow-500 mr-1" />
                          <span>1st</span>
                        </div>
                      ) : index === 1 ? (
                        <div className="flex items-center">
                          <Medal className="w-5 h-5 text-gray-400 fill-gray-400 mr-1" />
                          <span>2nd</span>
                        </div>
                      ) : index === 2 ? (
                        <div className="flex items-center">
                          <Medal className="w-5 h-5 text-amber-700 fill-amber-700 mr-1" />
                          <span>3rd</span>
                        </div>
                      ) : (
                        `${index + 1}th`
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {entry.username || 'Player'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {entry.hotelName}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap capitalize">
                      {entry.hotelType}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {entry.day}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      ${entry.money.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center">
                        {entry.reputation.toFixed(1)} 
                        <Star className="w-4 h-4 text-yellow-400 ml-1" />
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      {entry.marketShare}%
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right font-bold">
                      {entry.score.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* Score explanation */}
      <div className="mt-6 bg-blue-50 p-4 rounded-lg">
        <h3 className="font-bold mb-2 flex items-center">
          <Star className="w-5 h-5 text-yellow-500 mr-2" />
          How Scores Are Calculated
        </h3>
        <p className="text-sm text-gray-700">
          Scores are calculated based on your hotel's performance:
        </p>
        <ul className="mt-2 text-sm text-gray-600 space-y-1 ml-6 list-disc">
          <li>Money: $1 = 0.5 points</li>
          <li>Hotel Rating: 1★ = 1,000 points</li>
          <li>Days Survived: 1 day = 100 points</li>
          <li>Market Share: 1% = 500 points</li>
        </ul>
        <p className="mt-3 text-sm text-gray-700">
          Every saved game is automatically included in the leaderboard. 
          The more successful your hotel, the higher your score!
        </p>
      </div>
    </div>
  );
};

export default LeaderboardTab;